import { useCallback } from 'react';
import { useAppDispatch } from '../../core/hooks/storeHooks';
import { KonvaEventObject } from 'konva/lib/Node';
import { CellPosition, SelectionCell, SelectionOperationType } from '../types';
import {
  cellHover,
  mouseMoveWithSelection,
  startCellSelection,
  startSelectionInteraction,
  stopCellSelection,
} from '../redux/sagas/views/viewsActions';
import { isCtrlKey } from '../../core/utils/keys/keys';

type CellMouseDownORHover = (
  e: KonvaEventObject<MouseEvent>,
  cell: SelectionCell,
  position: CellPosition
) => void;

type HandleSelectionInteraction = (
  e: React.MouseEvent,
  op: SelectionOperationType
) => void;

type ActiveHeaderMouseDown = (
  e: KonvaEventObject<MouseEvent>,
  op: SelectionOperationType,
  positionIndex: number
) => void;

export interface UseCellSelection {
  onCellMouseDown: CellMouseDownORHover;
  onCellMouseHover: CellMouseDownORHover;
  handleSelectionInteraction: HandleSelectionInteraction;
  onActiveHeaderMouseDown: ActiveHeaderMouseDown;
}

export default (
  sheetId: string,
  freezedColsSum: number,
  freezedRowsSum: number
): UseCellSelection => {
  const dispatch = useAppDispatch();
  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      dispatch(
        mouseMoveWithSelection({
          sheetId,
          clientX: e.clientX,
          clientY: e.clientY,
          freezedColsSum,
          freezedRowsSum,
          ctrlKey: isCtrlKey(e.ctrlKey, e.metaKey),
        })
      );
    },
    [sheetId, freezedColsSum, freezedRowsSum]
  );

  const onMouseUp = (e: MouseEvent) => {
    dispatch(stopCellSelection(sheetId));
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  const onCellMouseDown = useCallback(
    (
      e: KonvaEventObject<MouseEvent>,
      cell: SelectionCell,
      position: CellPosition
    ) => {
      const { evt } = e;
      evt.preventDefault();
      const { ctrlKey, shiftKey, metaKey, buttons } = evt;

      switch (buttons) {
        case 1:
        case 2: {
          window.addEventListener('mouseup', onMouseUp);
          window.addEventListener('mousemove', onMouseMove);

          dispatch(
            startCellSelection({
              sheetId,
              position,
              cell,
              ctrlKey: isCtrlKey(ctrlKey, metaKey),
              shiftKey,
              forContextMenu: buttons === 2,
            })
          );
        }
      }
    },
    [sheetId, onMouseMove]
  );

  const onActiveHeaderMouseDown = useCallback(
    (
      e: KonvaEventObject<MouseEvent>,
      op: SelectionOperationType,
      positionIndex: number
    ) => {
      // console.log("rowIndex", positionIndex, op)
      const { evt } = e;
      evt.preventDefault();
      const { buttons } = evt;
      switch (buttons) {
        case 1: {
          window.addEventListener('mouseup', onMouseUp);
          window.addEventListener('mousemove', onMouseMove);

          dispatch(
            startSelectionInteraction({
              sheetId,
              op,
              clientX: 0,
              clientY: 0,
              freezedColsSum: 0,
              freezedRowsSum: 0,
              positionIndex,
            })
          );
        }
      }
    },
    [sheetId, onMouseMove]
  );

  const onCellMouseHover = useCallback(
    (
      e: KonvaEventObject<MouseEvent>,
      cell: SelectionCell,
      position: CellPosition
    ) => {
      const { evt } = e;
      evt.preventDefault();
      const { ctrlKey, shiftKey, metaKey, buttons } = evt;

      // 1 for left click 2 for right
      if (buttons === 1 || buttons === 2) {
        dispatch(
          cellHover({
            sheetId,
            cell,
            position,
            shiftKey,
            ctrlKey: isCtrlKey(ctrlKey, metaKey),
          })
        );
      }
    },
    [sheetId]
  );

  const handleSelectionInteraction = useCallback(
    (e: React.MouseEvent, op: SelectionOperationType) => {
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('mousemove', onMouseMove);
      // e.stopPropagation();
      e.preventDefault();

      // dispatch(switchWindow(sheetId));

      dispatch(
        startSelectionInteraction({
          op,
          sheetId,
          freezedColsSum: freezedColsSum,
          freezedRowsSum: freezedRowsSum,
          clientX: e.clientX,
          clientY: e.clientY,
        })
      );
    },
    [sheetId, freezedColsSum, freezedRowsSum]
  );

  return {
    onCellMouseDown,
    onCellMouseHover,
    handleSelectionInteraction,
    onActiveHeaderMouseDown,
  };
};
